// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { screen } from "@gcg/XDL/Helpers";
import Edit from "@gcg/XDL/Icons/Edit";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import { LinkPresentationProps } from "@insite/mobius/Link";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    currentPage: getCurrentPage(state),
});

interface OwnProps {
    cart?: Cart;
    /**
     * If true, the notes will display in a text field for editing.
     * If false, the notes will display as read-only text.
     * Default value: false
     */
    editable?: boolean;
    onNotesChange?: (updatedNotes: string) => void;
    label?: string;
    extendedStyles?: CartLineNotesStyles;
    showCutWire?: boolean;
}

type Props = OwnProps & HasCartLineContext & ReturnType<typeof mapStateToProps>;

export interface CartLineNotesStyles {
    wrapper?: InjectableCss;
    /** @deprecated */
    toggleLink?: LinkPresentationProps;
    label?: TypographyPresentationProps;
    icon?: IconPresentationProps;
    editableNotesTextArea?: TextFieldPresentationProps;
    readOnlyNotesText?: TypographyProps;
}

export const cartLineNotesStyles: CartLineNotesStyles = {
    wrapper: {
        css: css`
            width: 100%;
            line-height: 14px;
            display: flex;
            align-items: center;
            @media (max-width: 800px) {
                width: auto;
                flex-grow: 1;
            }
        `,
    },
    editableNotesTextArea: {
        cssOverrides: {
            formInputWrapper: css``,
            formField: css`
                &&&& input {
                    padding: 0 5px;
                    font-size: 12px;
                    line-height: 18px;
                    width: 325px;
                    border-color: ${getColor("common.white")};
                    height: auto !important;
                    text-overflow: ellipsis;
                    &:focus {
                        border-width: 1px;
                        border-color: #d7dbd9;
                        padding: 0 5px;
                        background: linear-gradient(270deg, rgba(244, 244, 244, 0.27) 0%, #e6e6e6 100%);
                        margin-left: 5px;
                        ::placeholder {
                            /* Chrome, Firefox, Opera, Safari 10.1+ */
                            color: ${getColor("text.main")};
                            opacity: 1; /* Firefox */
                            font-weight: normal;
                        }
                        :-ms-input-placeholder {
                            /* Internet Explorer 10-11 */
                            color: ${getColor("text.main")};
                            font-weight: normal;
                        }
                        ::-ms-input-placeholder {
                            /* Microsoft Edge */
                            color: ${getColor("text.main")};
                            font-weight: normal;
                        }
                    }
                    ::placeholder {
                        /* Chrome, Firefox, Opera, Safari 10.1+ */
                        color: ${getColor("text.main")};
                        opacity: 1; /* Firefox */
                        font-weight: bold;
                    }
                    :-ms-input-placeholder {
                        /* Internet Explorer 10-11 */
                        color: ${getColor("text.main")};
                        font-weight: bold;
                    }
                    ::-ms-input-placeholder {
                        /* Microsoft Edge */
                        color: ${getColor("text.main")};
                        font-weight: bold;
                    }

                    @media (max-width: 800px) {
                        width: 100%;
                    }
                }
            `,
        },
    },
    readOnlyNotesText: {
        css: css`
            padding: 0 5px;
            font-size: 12px;
            line-height: 18px;
            color: ${getColor("text.main")} !important;
        `,
    },
};

const CartLineNotes: FC<Props> = ({
    cart,
    cartLine,
    editable,
    onNotesChange = () => {},
    label,
    extendedStyles,
    showCutWire,
    currentPage,
}) => {
    const isOrderDetailsPage = currentPage.type === "OrderDetailsPage";
    const [notes, setNotes] = useState(cartLine.notes);
    let cutWire = [] as any;
    let cutWireNotes = "CUTS: ";
    if (cartLine.properties?.hasOwnProperty("cutWire") && cartLine.properties.cutWire !== "") {
        cutWire = JSON.parse(cartLine.properties.cutWire);
        // eslint-disable-next-line array-callback-return
        cutWire.map((line: any, index: number) => {
            cutWireNotes += `${line.cutCount}*${line.cutValue}/${cartLine.unitOfMeasure}${
                index + 1 !== cutWire.length ? "," : ""
            } `;
        });
    }

    React.useEffect(() => {
        if (showCutWire && cutWire.length > 0 && !cartLine.notes.includes(cutWireNotes)) {
            const notes = `${cutWireNotes} ${cartLine.notes}`;
            setNotes(notes);
        }

        // when the user redirects back from quote to cart page, remove the cuts defined from the notes
        if (currentPage.type === "CartPage" && cutWire.length > 0 && cartLine.notes.includes(cutWireNotes)) {
            const notes = cartLine.notes.replace(cutWireNotes, "");
            setNotes(notes.trim());
        }
    }, []);

    React.useEffect(() => {
        if (notes !== cartLine.notes) {
            setNotes(cartLine.notes);
        }
    }, [cartLine.notes]);

    const changeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setNotes(event.currentTarget.value);
    };

    const blurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        onNotesChange(notes);
        if (notes.length > 0) {
            event.currentTarget.style.background =
                "linear-gradient(270deg, rgba(244, 244, 244, 0.27) 0%, #e6e6e6 100%)";
            event.currentTarget.style.borderColor = "#d7dbd9";
            event.currentTarget.style.marginLeft = "5px";
        }
    };

    const heading = "Add Product Note";

    const [styles] = React.useState(() => mergeToNew(cartLineNotesStyles, extendedStyles));

    return (
        <StyledWrapper {...styles.wrapper}>
            {!isOrderDetailsPage && (
                <Icon src={Edit} size={15} color="primary.main" />
            )}
            {editable && (
                <TextField
                    {...styles.editableNotesTextArea}
                    style={
                        notes.length > 0
                            ? {
                                  background: "linear-gradient(270deg, rgba(244, 244, 244, 0.27) 0%, #e6e6e6 100%)",
                                  borderColor: "#d7dbd9",
                                  marginLeft: "5px",
                              }
                            : undefined
                    }
                    value={notes}
                    id={`notes-${cartLine.id}`}
                    maxLength={300}
                    placeholder={translate("Add Product Note")}
                    onChange={event => changeHandler(event)}
                    onBlur={event => blurHandler(event)}
                    data-test-selector="cartline_notes"
                />
            )}
            {!editable && (
                <Typography {...styles.readOnlyNotesText} data-test-selector="cartline_notes_disabled">
                    {notes}
                </Typography>
            )}
        </StyledWrapper>
    );
};

export default withCartLine(connect(mapStateToProps)(CartLineNotes));
